// Deterministic, local milestone suggestion scoring for the Add/Edit Task workflow.
// No external/LLM calls — purely in-memory over already-loaded launch milestones.
import { ukToday } from "@/data/taskOptions";

const STOP = new Set([
  "the", "a", "an", "and", "or", "to", "of", "for", "with", "in", "on", "at", "by",
  "is", "are", "be", "this", "that", "our", "your", "from", "up", "out", "as", "it", "we",
]);

export const tokenize = (text) =>
  (text || "")
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, " ")
    .split(/\s+/)
    .filter((w) => w.length >= 3 && !STOP.has(w));

// task workstream -> candidate milestone workstreams
const WS_MAP = {
  "Product and Content": ["Product", "Content"],
  Launch: ["Launch"],
  Marketing: ["Marketing"],
  Investors: ["Commercial"],
  Finance: ["Commercial"],
  "Future Innovations": ["Product"],
};

// related-record type -> candidate milestone workstreams
const REL_WS = {
  related_module_id: ["Product", "Content"],
  related_chapter_id: ["Product", "Content"],
  related_campaign_id: ["Marketing"],
  related_investor_id: ["Commercial"],
  related_innovation_id: ["Product"],
};

// Returns up to `limit` most relevant active milestones (best first).
export function suggestMilestones(milestones, ctx, limit = 3) {
  const corpus = [ctx.title, ctx.description, ctx.notes, ...(ctx.related || []).map((r) => r.name)].join(" ");
  const taskTokens = new Set(tokenize(corpus));

  const wsSet = new Set(WS_MAP[ctx.workstream] || []);
  (ctx.related || []).forEach((r) => (REL_WS[r.key] || []).forEach((w) => wsSet.add(w)));

  const today = ukToday();
  const scored = [];
  for (const m of milestones || []) {
    if (m.archived) continue;              // never suggest archived
    if (m.status === "Cancelled") continue; // never suggest cancelled

    const mTokens = tokenize(m.title);
    let overlap = 0;
    for (const t of mTokens) if (taskTokens.has(t)) overlap += 1;

    const offFramework = m.off_framework || (m.readiness_weight ?? 1) === 0;
    if (offFramework && overlap < 2) continue; // off-framework weight-0 only on a clear match
    if (overlap < 1) continue;                 // require at least one shared keyword

    let score = overlap * 3;
    if (m.workstream && wsSet.has(m.workstream)) score += 4;

    if (m.status === "Complete") score -= 6;                     // strongly deprioritise
    else if (m.status === "In progress") score += 3;
    else if (m.due_date && m.due_date >= today) score += 2;       // upcoming
    else if (m.due_date && m.due_date < today) score += 1;        // active but overdue

    if (score >= 5) scored.push({ m, score });
  }

  scored.sort((a, b) => b.score - a.score || (a.m.due_date || "9999").localeCompare(b.m.due_date || "9999"));
  return scored.slice(0, limit).map((s) => s.m);
}
